// Pure comparison logic for the "Compare Scenario" tab: given two saved
// `what-if-scenario-to-be` records (see what_if_scenario_to_be_documentation.md
// for the real field names), produce the side-by-side figures the tab
// renders. No fetching, no React — same split already used by
// whatIfScenario.js and whatIfQueueing.js.
//
// The schema stores less than a live queuing run does: no arrival rate, no
// weekly capacity, no utilization %, no stability flag, no bottleneck marker
// — only `staff`, `servicePerCase`, `WeeklyLoad` (workload hours) and
// `queueWaitingPerCase` per job. Everything below is built from only those
// fields; nothing here fabricates a number the backend doesn't actually
// persist.

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

function compareToBe(valueA, valueB) {
  const a = toNumber(valueA);
  const b = toNumber(valueB);
  const delta = b - a;
  return { a, b, delta, percent: a !== 0 ? (delta / Math.abs(a)) * 100 : 0 };
}

const average = (values) => {
  const nums = (values || []).filter((v) => v != null).map((v) => toNumber(v));
  if (!nums.length) return null;
  return nums.reduce((sum, v) => sum + v, 0) / nums.length;
};

// The `job` relation's name field is `name` (e.g. `{ job_id: 2440, name:
// "Marketing Analyst" }`), on both `raciMatrixToBe` and `queuingAnalysis`
// items — confirmed against a live GET response. `job_name`/top-level
// `job_name` are kept as fallbacks in case a future response shape differs,
// and the raciMatrixToBe-built lookup covers the (currently theoretical)
// case where a queuing row's own `job` isn't included.
function buildJobNameMap(scenario) {
  const map = new Map();
  (scenario?.raciMatrixToBe || []).forEach((item) => {
    const name = item?.job?.name || item?.job?.job_name || item?.job_name;
    if (item?.job_id != null && name) map.set(String(item.job_id), name);
  });
  return map;
}

const FALLBACK_PREFIX = "Job ";

const jobLabel = (item, nameMap) =>
  item?.job?.name ||
  nameMap?.get(String(item?.job_id)) ||
  item?.job?.job_name ||
  item?.job_name ||
  `${FALLBACK_PREFIX}${item?.job_id ?? "?"}`;

// Every job that appears in either scenario's queuing rows, so a job staffed
// in only one scenario shows as an addition/removal rather than vanishing.
function mergeQueuingRows(itemsA, itemsB, namesA, namesB) {
  const byJob = new Map();
  (itemsA || []).forEach((item) => {
    byJob.set(String(item.job_id), {
      jobId: item.job_id,
      jobName: jobLabel(item, namesA),
      a: item,
      b: null,
    });
  });
  (itemsB || []).forEach((item) => {
    const key = String(item.job_id);
    const existing = byJob.get(key);
    const nameB = jobLabel(item, namesB);
    if (existing) {
      existing.b = item;
      // Whichever side actually resolved a real name (rather than the
      // "Job {id}" fallback) wins — the two scenarios' RACI matrices may not
      // both mention this job.
      if (existing.jobName.startsWith(FALLBACK_PREFIX) && !nameB.startsWith(FALLBACK_PREFIX)) {
        existing.jobName = nameB;
      }
    } else {
      byJob.set(key, { jobId: item.job_id, jobName: nameB, a: null, b: item });
    }
  });
  return [...byJob.values()];
}

/**
 * Compares two saved scenarios (same process, different staffing/duration
 * edits). `scenarioA.queuingAnalysis`/`scenarioB.queuingAnalysis` may be
 * empty — a scenario saved without ever running a queuing analysis — and
 * every queuing figure below degrades to `null` rather than a fabricated
 * zero.
 *
 * `asIs` is the process's live baseline — `{ laborCostPerCase, cycleTimeMinutes,
 * staffingCostUsd }`, read from `GET /cms/whatif/{processId}` (see
 * CompareScenarioTab.js) — since neither scenario's own saved record carries
 * an as-is figure for Cycle Time or Staffing Cost (only Labour Cost has one
 * stored, `currentProcessCost`, used as a fallback below if the live fetch
 * wasn't available). `cycleTimeMinutes` must come from `metrics.cycle_time_minutes`
 * specifically — NOT `metrics.original_effort_time_minutes`, which is a
 * different figure (total participant-attributed minutes across every RACI
 * role, gross of eliminated tasks — a labor-hours total, not a process
 * duration; see project notes). Both scenarios share the same process, so
 * this is fetched once by the caller rather than per scenario.
 */
export function compareScenarios(scenarioA, scenarioB, asIs = {}) {
  const metrics = {
    laborCostPerCaseUsd: {
      asIs: toNumber(
        asIs.laborCostPerCase ?? scenarioA?.currentProcessCost ?? scenarioB?.currentProcessCost,
      ),
      ...compareToBe(scenarioA?.labourCostToBe, scenarioB?.labourCostToBe),
    },
    // `cycleTimeToBe` on the saved scenario now holds the true anchor-and-
    // adjust Cycle Time (see whatIfScenarioToBe.js), not the old flat-task
    // "Effort" sum.
    cycleTimeMinutes: {
      asIs: asIs.cycleTimeMinutes != null ? toNumber(asIs.cycleTimeMinutes) : null,
      ...compareToBe(scenarioA?.cycleTimeToBe, scenarioB?.cycleTimeToBe),
    },
    staffingCostUsd: {
      asIs: asIs.staffingCostUsd != null ? toNumber(asIs.staffingCostUsd) : null,
      ...compareToBe(scenarioA?.staffCostToBe, scenarioB?.staffCostToBe),
    },
  };

  const qA = scenarioA?.queuingAnalysis || [];
  const qB = scenarioB?.queuingAnalysis || [];
  const hasA = qA.length > 0;
  const hasB = qB.length > 0;
  const namesA = buildJobNameMap(scenarioA);
  const namesB = buildJobNameMap(scenarioB);

  const queuing =
    !hasA && !hasB
      ? null
      : {
          hasA,
          hasB,
          totalStaffA: hasA ? qA.reduce((sum, i) => sum + toNumber(i.staff, 1), 0) : null,
          totalStaffB: hasB ? qB.reduce((sum, i) => sum + toNumber(i.staff, 1), 0) : null,
          totalWeeklyLoadHoursA: hasA
            ? qA.reduce((sum, i) => sum + toNumber(i.WeeklyLoad), 0)
            : null,
          totalWeeklyLoadHoursB: hasB
            ? qB.reduce((sum, i) => sum + toNumber(i.WeeklyLoad), 0)
            : null,
          avgQueueWaitMinutesA: hasA ? average(qA.map((i) => i.queueWaitingPerCase)) : null,
          avgQueueWaitMinutesB: hasB ? average(qB.map((i) => i.queueWaitingPerCase)) : null,
          jobs: mergeQueuingRows(qA, qB, namesA, namesB),
        };

  return { metrics, queuing };
}
